import React, { useState, useRef, useEffect } from 'react';
import SupportModal from './support/SupportModal';

/**
 * Navbar — Top navigation bar for MinuteWinIt.
 *
 * Displays:
 *   - Brand + view switcher (Trading Floor / Ledger / Admin)
 *   - Session stats (Win / Loss)
 *   - Controls (Clear Session, Bot Bets, Stop Clock)
 *   - Demo Balance + House Fee readouts
 *   - User dropdown with Sign Out
 */
const Navbar = ({
    view,
    setView,
    totalWinNet,
    totalLossNet,
    ledger = [],
    balance,
    houseFee,
    isPaused,
    setIsPaused,
    onClearSession,
    username,
    onLogout,
    isAdmin,
    walletAddress,
    walletSolBalance,
    onConnectWallet,
    onDisconnectWallet,
    walletConnecting,
    onDeposit,
    onWithdraw,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [depositAmount, setDepositAmount] = useState('');
    const [depositing, setDepositing] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawWallet, setWithdrawWallet] = useState('');
    const [withdrawing, setWithdrawing] = useState(false);
    const [withdrawMsg, setWithdrawMsg] = useState('');
    const [winOpen, setWinOpen] = useState(false);
    const [lossOpen, setLossOpen] = useState(false);
    const [supportOpen, setSupportOpen] = useState(false);
    const dropdownRef = useRef(null);
    const winRef = useRef(null);
    const lossRef = useRef(null);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
            if (winRef.current && !winRef.current.contains(e.target)) setWinOpen(false);
            if (lossRef.current && !lossRef.current.contains(e.target)) setLossOpen(false);
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const handleLoadCredits = async () => {
        const amount = parseFloat(depositAmount);
        if (isNaN(amount) || amount <= 0 || !onDeposit) return;
        setDepositing(true);
        try {
            await onDeposit(amount);
            setDepositAmount('');
        } catch (err) {
            alert('Deposit failed: ' + err.message);
        } finally {
            setDepositing(false);
        }
    };

    const handleCashOut = async () => {
        const credits = Math.floor(parseFloat(withdrawAmount));
        if (!credits || credits < 100 || credits % 100 !== 0) {
            setWithdrawMsg('Minimum 100 credits, must be a multiple of 100.');
            return;
        }
        if (!withdrawWallet.trim()) {
            setWithdrawMsg('Enter a destination wallet address.');
            return;
        }
        if (!onWithdraw) return;
        setWithdrawing(true);
        setWithdrawMsg('');
        try {
            await onWithdraw(credits, withdrawWallet.trim());
            setWithdrawAmount('');
            setWithdrawWallet('');
            setWithdrawMsg(`✅ ${credits / 100} SOL sent!`);
        } catch (err) {
            setWithdrawMsg('Cash out failed: ' + err.message);
        } finally {
            setWithdrawing(false);
        }
    };

    const winBets = ledger.filter(e => e.result === 'WIN');
    const lossBets = ledger.filter(e => e.result === 'LOSS');

    return (
        <>
            <div className="navbar-glass flex justify-between items-center px-4 py-2 min-h-[44px]">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-1.5 text-base tracking-[0.18em] uppercase font-black text-white">
                        <span>Minute</span>
                        <img src="/logo-icon.png" alt="" className="w-5 h-5 opacity-90" />
                        <span style={{ color: 'var(--cyan)' }}>Market</span>
                    </div>
                    <nav className="flex gap-1">
                        {/* Trading Floor — hidden on mobile (it's the default view) */}
                        <button
                            onClick={() => setView('trading')}
                            className={`hidden sm:block text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-md transition-all ${
                                view === 'trading'
                                    ? 'text-[#00e5cc] bg-[rgba(0,229,204,0.1)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                            }`}
                        >
                            Trading Floor
                        </button>
                        {/* Poly Predict — external page, standalone */}
                        <a
                            href="/polypredic.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden sm:block text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-md transition-all text-gray-400 hover:text-white hover:bg-white/[0.04]"
                            style={{ textDecoration: 'none' }}
                        >
                            Poly ↗
                        </a>
                        {/* Ledger ↔ Trading Floor toggle — always visible on all screen sizes.
                            On the Ledger page it flips to "Trading Floor" so mobile users
                            can return without tapping the browser back button. */}
                        <button
                            onClick={() => setView(view === 'dashboard' ? 'trading' : 'dashboard')}
                            className={`text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-md transition-all ${
                                view === 'dashboard'
                                    ? 'text-[#00e5cc] bg-[rgba(0,229,204,0.1)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                            }`}
                        >
                            {view === 'dashboard' ? '← Trading Floor' : 'Ledger'}
                        </button>
                        {/* Admin — desktop only, admin accounts only */}
                        {isAdmin && (
                            <button
                                onClick={() => setView('admin')}
                                className={`hidden sm:block text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-md transition-all ${
                                    view === 'admin'
                                        ? 'text-amber-400 bg-amber-500/10'
                                        : 'text-amber-500/60 hover:text-amber-400 hover:bg-amber-500/5'
                                }`}
                            >
                                ⚙ Admin
                            </button>
                        )}
                    </nav>
                </div>

                <div className="flex items-center gap-2">

                    {/* ── Unified stat pill: WIN | LOSS | Credits ── */}
                    <div className="hidden sm:flex items-stretch h-[34px] rounded-xl border border-white/[0.08] bg-white/[0.03] overflow-hidden">

                        {/* WIN */}
                        <div className="relative" ref={winRef}>
                            <button
                                onClick={() => { setWinOpen(!winOpen); setLossOpen(false); }}
                                className="flex items-center gap-1.5 px-3 h-[34px] hover:bg-green-500/5 transition-colors"
                            >
                                <span className="text-green-400/50 text-[9px] font-black">▲</span>
                                <span className="font-mono text-green-400 text-xs font-bold" style={{fontFamily:"'JetBrains Mono',monospace"}}>
                                    {Math.abs(parseFloat((totalWinNet * 100).toFixed(2)))}
                                </span>
                            </button>
                            {winOpen && winBets.length > 0 && (
                                <div className="absolute top-full left-0 mt-1 w-72 max-h-80 overflow-y-auto rounded-lg border border-green-500/20 bg-[#0c0c1a] shadow-[0_10px_40px_rgba(0,0,0,0.6)] z-50">
                                    <div className="px-3 py-2 border-b border-white/10 text-[9px] uppercase tracking-widest text-green-500/70 font-black">
                                        {winBets.length} Winning Bet{winBets.length !== 1 ? 's' : ''}
                                    </div>
                                    {winBets.map((bet, i) => (
                                        <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-white/[0.03] hover:bg-white/[0.02]">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] uppercase font-bold text-white">{bet.coin?.slice(0, 4)}</span>
                                                <span className={`text-[9px] font-bold ${bet.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                                                    {bet.direction === 'UP' ? '▲' : '▼'}
                                                </span>
                                                <span className="text-[10px] text-gray-400 font-mono">{bet.displayTime || bet.time}</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs font-mono text-white">{Math.round(bet.amount * 100)}</div>
                                                <div className="text-[10px] font-mono text-green-400">+{Math.abs(parseFloat((bet.net * 100).toFixed(2)))}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="w-px bg-white/[0.07] self-stretch" />

                        {/* LOSS */}
                        <div className="relative" ref={lossRef}>
                            <button
                                onClick={() => { setLossOpen(!lossOpen); setWinOpen(false); }}
                                className="flex items-center gap-1.5 px-3 h-[34px] hover:bg-red-500/5 transition-colors"
                            >
                                <span className="text-red-400/50 text-[9px] font-black">▼</span>
                                <span className="font-mono text-red-400 text-xs font-bold" style={{fontFamily:"'JetBrains Mono',monospace"}}>
                                    {Math.round(totalLossNet * 100)}
                                </span>
                            </button>
                            {lossOpen && lossBets.length > 0 && (
                                <div className="absolute top-full right-0 mt-1 w-72 max-h-80 overflow-y-auto rounded-lg border border-red-500/20 bg-[#0c0c1a] shadow-[0_10px_40px_rgba(0,0,0,0.6)] z-50">
                                    <div className="px-3 py-2 border-b border-white/10 text-[9px] uppercase tracking-widest text-red-500/70 font-black">
                                        {lossBets.length} Losing Bet{lossBets.length !== 1 ? 's' : ''}
                                    </div>
                                    {lossBets.map((bet, i) => (
                                        <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-white/[0.03] hover:bg-white/[0.02]">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] uppercase font-bold text-white">{bet.coin?.slice(0, 4)}</span>
                                                <span className={`text-[9px] font-bold ${bet.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                                                    {bet.direction === 'UP' ? '▲' : '▼'}
                                                </span>
                                                <span className="text-[10px] text-gray-400 font-mono">{bet.displayTime || bet.time}</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs font-mono text-white">{Math.round(bet.amount * 100)}</div>
                                                <div className="text-[10px] font-mono text-red-400">-{Math.round(bet.amount * 100)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="w-px bg-white/[0.07] self-stretch" />

                        {/* Credits */}
                        <div className="flex items-center gap-2 px-3 h-[34px]">
                            <span className="text-[8px] uppercase tracking-widest font-black text-white/25">Credits</span>
                            <span className="font-mono text-xs font-bold" style={{fontFamily:"'JetBrains Mono',monospace", color:'var(--blue)'}}>
                                {parseFloat((balance * 100).toFixed(2)).toLocaleString()}
                            </span>
                        </div>

                        {/* Revenue — admin only, inside pill */}
                        {isAdmin && (
                            <>
                                <div className="w-px bg-white/[0.07] self-stretch" />
                                <div className="hidden md:flex items-center gap-2 px-3 h-[34px]">
                                    <span className="text-[8px] uppercase tracking-widest font-black text-[#f4c94e]/30">Rev</span>
                                    <span className="font-mono text-xs font-bold text-[#f4c94e]" style={{fontFamily:"'JetBrains Mono',monospace"}}>
                                        {(houseFee * 100).toFixed(2)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── Clear — icon button ── */}
                    <button
                        onClick={onClearSession}
                        title="Clear session"
                        className="hidden sm:flex items-center justify-center w-[34px] h-[34px] rounded-xl border border-white/[0.08] bg-white/[0.03] text-white/25 hover:text-amber-400 hover:border-amber-500/30 hover:bg-amber-500/[0.08] transition-all text-xs"
                    >
                        ✕
                    </button>

                    {/* ── Stop Clock — admin only ── */}
                    {isAdmin && (
                        <button
                            onClick={() => setIsPaused(!isPaused)}
                            className={`hidden sm:flex items-center gap-1.5 h-[34px] px-3 rounded-xl border transition-all text-[9px] tracking-widest font-black uppercase ${
                                isPaused
                                    ? 'bg-red-500/20 border-red-500/50 text-red-400 shadow-[0_0_14px_rgba(239,68,68,0.2)]'
                                    : 'border-white/[0.08] bg-white/[0.03] text-white/30 hover:text-[#f4c94e] hover:border-[#f4c94e]/30 hover:bg-[#f4c94e]/[0.06]'
                            }`}
                        >
                            {isPaused ? '▪ Stopped' : '⏸ Clock'}
                        </button>
                    )}

                    {/* User Dropdown */}
                    {username && (
                        <div className="relative ml-1 pl-3 border-l border-white/[0.06]" ref={dropdownRef}>
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="flex items-center gap-2 text-[10px] text-gray-300 hover:text-white font-bold tracking-wide transition-colors py-1"
                            >
                                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#4d9fff] to-[#00e5cc] flex items-center justify-center text-[10px] text-white font-black border border-[#00e5cc]/30">
                                    {username.charAt(0).toUpperCase()}
                                </span>
                                <span className="hidden sm:block">{username}</span>
                                <span className={`text-[8px] text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}>
                                    ▼
                                </span>
                            </button>

                            {dropdownOpen && (
                                <div className="fixed top-[45px] w-72 rounded-lg border border-white/10 bg-[#0c0c1a] shadow-[0_10px_40px_rgba(0,0,0,0.6)] overflow-hidden z-50" style={{ right: '8px' }}>
                                    {isAdmin && (
                                        <div className="px-5 py-3 border-b border-white/5">
                                            <span className="text-sm px-3 py-1 rounded bg-amber-500/10 text-amber-400 font-black uppercase tracking-wider">
                                                Admin
                                            </span>
                                        </div>
                                    )}
                                    {walletAddress ? (
                                        <button
                                            onClick={() => { onDisconnectWallet && onDisconnectWallet(); }}
                                            className="w-full px-5 py-3.5 border-b border-white/5 flex items-center gap-3 hover:bg-red-500/10 transition-all group/wallet bg-white/[0.03]"
                                        >
                                            <span className="w-8 h-8 rounded-full bg-cyan-400/20 flex items-center justify-center text-base text-cyan-300 group-hover/wallet:bg-red-500/20 group-hover/wallet:text-red-400 transition-colors">◆</span>
                                            <div className="flex flex-col items-start">
                                                <span className="text-base text-cyan-300 font-mono font-semibold group-hover/wallet:text-red-400 transition-colors">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
                                                <span className="text-sm text-white/80 font-mono font-bold">{(walletSolBalance ?? 0).toFixed(2)} SOL</span>
                                            </div>
                                            <span className="text-base text-gray-500 group-hover/wallet:text-red-400 ml-auto transition-colors">✕</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => { onConnectWallet && onConnectWallet(); }}
                                            disabled={walletConnecting}
                                            className="w-full px-5 py-4 text-left text-sm text-gray-300 hover:text-cyan-400 hover:bg-white/[0.03] transition-all uppercase tracking-[0.15em] font-bold flex items-center gap-3 border-b border-white/5"
                                        >
                                            <span className="text-base">◆</span> {walletConnecting ? 'Connecting...' : 'Connect Wallet'}
                                        </button>
                                    )}
                                    {/* Load Credits */}
                                    <div className="px-5 py-4 border-b border-white/5">
                                        <div className="text-sm uppercase tracking-[0.1em] text-emerald-400 font-bold mb-1.5">
                                            Load Credits
                                        </div>
                                        <div className="text-xs text-gray-400 mb-3">
                                            1 SOL = 100 Credits
                                        </div>
                                        {walletAddress ? (
                                            <>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs text-gray-400">Available: <span className="text-white font-mono font-bold">{(walletSolBalance ?? 0).toFixed(4)} SOL</span></span>
                                                    <button
                                                        onClick={() => setDepositAmount(String(walletSolBalance ?? 0))}
                                                        disabled={!walletSolBalance || walletSolBalance <= 0}
                                                        className="text-[10px] uppercase tracking-wider text-cyan-400 hover:text-cyan-300 font-bold px-2 py-0.5 border border-cyan-500/30 rounded hover:bg-cyan-500/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                                    >
                                                        MAX
                                                    </button>
                                                </div>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="Enter SOL amount"
                                                    value={depositAmount}
                                                    onChange={(e) => {
                                                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                                                        setDepositAmount(raw);
                                                    }}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleLoadCredits()}
                                                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2.5 text-white font-mono text-base focus:outline-none focus:border-emerald-500/50 mb-2"
                                                />
                                                {depositAmount && parseFloat(depositAmount) > 0 && (
                                                    <div className="text-sm text-emerald-400/70 font-mono mb-2">
                                                        = {(parseFloat(depositAmount) * 100).toLocaleString()} Credits
                                                    </div>
                                                )}
                                                <button
                                                    onClick={handleLoadCredits}
                                                    disabled={depositing || !depositAmount || parseFloat(depositAmount) <= 0}
                                                    className="w-full py-2.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 rounded font-bold text-sm uppercase tracking-wider hover:bg-emerald-500/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                                >
                                                    {depositing ? 'Loading...' : '◆ Load Credits'}
                                                </button>
                                            </>
                                        ) : (
                                            <div className="text-sm text-gray-500 italic">
                                                Connect wallet first
                                            </div>
                                        )}
                                    </div>
                                    {/* Cash Out */}
                                    <div className="px-5 py-4 border-b border-white/5">
                                        <div className="text-sm uppercase tracking-[0.1em] text-amber-400 font-bold mb-1.5">
                                            Cash Out
                                        </div>
                                        <div className="text-xs text-gray-400 mb-3">
                                            100 Credits = 1 SOL &bull; multiples of 100 only
                                        </div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                placeholder="Credits to withdraw"
                                                value={withdrawAmount}
                                                onChange={(e) => {
                                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                                    setWithdrawAmount(raw);
                                                    setWithdrawMsg('');
                                                }}
                                                className="flex-1 bg-black/50 border border-white/10 rounded px-3 py-2 text-white font-mono text-sm focus:outline-none focus:border-amber-500/50"
                                            />
                                            <button
                                                onClick={() => {
                                                    const maxCredits = Math.floor(balance * 100);
                                                    const floored = Math.floor(maxCredits / 100) * 100;
                                                    setWithdrawAmount(String(floored));
                                                }}
                                                className="text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300 font-bold px-2 py-1 border border-amber-500/30 rounded hover:bg-amber-500/10 transition-all"
                                            >
                                                MAX
                                            </button>
                                        </div>
                                        {withdrawAmount && parseInt(withdrawAmount) >= 100 && (
                                            <div className="text-xs text-amber-400/70 font-mono mb-2">
                                                = {(parseInt(withdrawAmount) / 100).toFixed(2)} SOL
                                            </div>
                                        )}
                                        <input
                                            type="text"
                                            placeholder="Destination wallet address"
                                            value={withdrawWallet}
                                            onChange={(e) => setWithdrawWallet(e.target.value)}
                                            className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-white font-mono text-xs focus:outline-none focus:border-amber-500/50 mb-2"
                                        />
                                        <button
                                            onClick={handleCashOut}
                                            disabled={withdrawing || !withdrawAmount || parseInt(withdrawAmount) < 100}
                                            className="w-full py-2.5 bg-amber-500/20 border border-amber-500/40 text-amber-400 rounded font-bold text-sm uppercase tracking-wider hover:bg-amber-500/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                            {withdrawing ? 'Processing...' : '↑ Cash Out'}
                                        </button>
                                        {withdrawMsg && (
                                            <p className="text-xs mt-2 font-mono text-center"
                                                style={{ color: withdrawMsg.startsWith('✅') ? '#4ade80' : '#f87171' }}>
                                                {withdrawMsg}
                                            </p>
                                        )}
                                    </div>
                                    {/* Support button */}
                                    <button
                                        onClick={() => { setDropdownOpen(false); setSupportOpen(true); }}
                                        className="w-full px-5 py-3 text-left text-sm text-gray-400 hover:text-cyan-400 hover:bg-white/[0.03] transition-all uppercase tracking-[0.1em] font-bold flex items-center gap-3 border-b border-white/5"
                                    >
                                        <span className="text-base">🎫</span> Support
                                    </button>
                                    <button
                                        onClick={() => { setDropdownOpen(false); onLogout(); }}
                                        className="w-full px-5 py-4 text-left text-sm text-gray-400 hover:text-red-400 hover:bg-white/[0.03] transition-all uppercase tracking-[0.1em] font-bold flex items-center gap-3"
                                    >
                                        <span className="text-base">↩</span> Sign Out
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <SupportModal
                isOpen={supportOpen}
                onClose={() => setSupportOpen(false)}
                user={{ email: username }}
            />
        </>
    );
};

export default Navbar;
